import React, { useEffect, useMemo } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { LoggerProvider } from "./context/LoggerContext"; 
import { ErrorBoundaryProvider } from "./context/ErrorBoundaryContext";
import ErrorNotification from "./components/ErrorNotification";
import ScrollRestoration from "components/ScrollRestoration"; 
import { SeoProvider } from "context/SeoContext";
import { setContrastColors } from "./helpers/contrastCalculator";

// Import of the new URL configuration
import { organizationUniqueUrl, siteUniqueUrl, moduleType, pathModuleType } from "config/urlConfig";
import { globalSeoDefaults } from "config/seoDefaults"; 

import Home from "pages/Home";
import NotFound from "components/NotFound";
import DynamicContent from "components/DynamicContent";
import RoutePrefixWrapper from "components/RoutePrefixWrapper";
import MetaManager from "components/MetaManager";

// Add the CartProvider here
import { CartProvider } from 'context/CartContext';

// Helper function for redirection
const RedirectWrapper = ({ pathModuleType }) => {
	const { redirectPath } = pathModuleType;
	return (
		<Navigate 
			to={params => `/${params["*"].split('/')[0]}/${redirectPath}`} 
			replace
		/>
	);
};

const App = () => {
	// Set up contrast colors when app loads and when CSS variables change
	useEffect(() => {
		setContrastColors();
		
		// Create a MutationObserver to watch for styles changes
		const observer = new MutationObserver(() => {
			setContrastColors();
		});
		
		// Observe changes to style attributes
		observer.observe(document.documentElement, { 
			attributes: true,
			attributeFilter: ['style', 'class']
		});
		
		return () => observer.disconnect();
	}, []);

	// Memoize the routing to prevent unnecessary re-renders
	const renderRoutes = useMemo(() => {
		// Check if both unique_url values exist:
		const useRootRouting = Boolean(organizationUniqueUrl && siteUniqueUrl);
		// Check if we are on a module subdomain
		const isModuleSubdomain = Boolean(moduleType);
		// Check if pathModuleType is enabled for direct paths
		const usePathModule = Boolean(pathModuleType);
		
		// Return the appropriate routing
		if (isModuleSubdomain) {
			return (
				<>
					<Route path="/:org_url/:unique_url" element={<DynamicContent isModuleRoute={true} moduleType={moduleType} />} />
					<Route path="/:org_url" element={<DynamicContent isModuleRoute={true} moduleType={moduleType} />} />
					<Route path="/*" element={<DynamicContent isModuleRoute={true} moduleType={moduleType} />} />
				</>
			);
		} else if (useRootRouting) {
			return (
				<>
					<Route path="/*" element={<DynamicContent />} />
					<Route path="*" element={<NotFound />} />
				</>
			);
		} else if (usePathModule) {
			return (
				<>
					{/* Route for detail view */}
					<Route path="/:org_url/:unique_url" element={<DynamicContent usePathModule={true} pathModuleType={pathModuleType} />} />
					
					{/* Route for organization page: 
                       - For static pages (isStaticPage) always use DynamicContent directly
                       - For other modules with redirectPath: use redirection
                       - For modules with hasListView: show list view
                    */}
					<Route 
						path="/:org_url" 
						element={
							pathModuleType.isStaticPage ? (
								<DynamicContent 
									usePathModule={true} 
									pathModuleType={pathModuleType} 
								/>
							) : pathModuleType.redirectPath ? (
								<RedirectWrapper pathModuleType={pathModuleType} /> 
							) : (
								<DynamicContent 
									usePathModule={true} 
									pathModuleType={pathModuleType} 
									showListView={pathModuleType.hasListView} 
								/>
							)
						} 
					/>
					
					<Route path="/*" element={<Home />} />
					<Route path="*" element={<NotFound />} />
				</>
			);
		} else {
			return (
				<>
					<Route path="/*" element={<Home />} />
					<Route path=":org_url/*" element={<RoutePrefixWrapper />}>
						<Route path=":site_url/*" element={<DynamicContent />} />
					</Route>
					<Route path="*" element={<NotFound />} />
				</>
			);
		}
	}, []);

	return (
		<ThemeProvider>
			<LoggerProvider>
				<ErrorBoundaryProvider>
					<SeoProvider seoDefaults={globalSeoDefaults}>
						<HelmetProvider>
							{/* Global MetaManager as fallback */}
							<MetaManager {...globalSeoDefaults} />
							<Router>
								<ScrollRestoration />
								<CartProvider>
									{/* Global error notification component */}
									<ErrorNotification />
									<Routes>
										{renderRoutes}
									</Routes>
								</CartProvider>
							</Router>
						</HelmetProvider>
					</SeoProvider>
				</ErrorBoundaryProvider>
			</LoggerProvider>
		</ThemeProvider>
	);
};

export default App;
